import React, { useEffect, useMemo, useRef, useState } from "react";
import { RiArrowDownSLine, RiArrowRightSLine, RiSearchLine } from "react-icons/ri";
import { Checkbox, Input } from "@/components/ui";
import Button from "@/components/ui/button";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { cx, hasErrorInput } from "@/lib/tailwind-utils";
import { FilterOption } from "./types";
interface MultiSelectInputProps {
  value?: any;
  onChange: (value: any) => void;
  ref?: React.Ref<HTMLElement>;
  options?: FilterOption[];
  columns?: number;
  hasError?: boolean;
  errorMessage?: string;
  enableCustomOther?: boolean;
  showBulkActions?: boolean;
  /** When true, changes are deferred until an Apply button is clicked */
  deferApply?: boolean;
}

/** Group structure for organizing options */
interface OptionGroup {
  name: string;
  options: FilterOption[];
}
const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  value = [],
  onChange,
  options,
  columns,
  hasError,
  errorMessage,
  enableCustomOther,
  showBulkActions = true,
  deferApply = false
}) => {
  const {
    isMobile
  } = useIsMobile();

  // Separate state for UI controls
  const [isOtherChecked, setIsOtherChecked] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMobile) {
      searchInputRef.current?.focus();
    }
  }, [isMobile]);

  // Local state for deferred apply mode
  const [pendingValue, setPendingValue] = useState<any[]>(value);

  // Sync pending value with external value when it changes (e.g., on clear)
  useEffect(() => {
    setPendingValue(value);
  }, [value]);

  // Determine which value to use for display/state
  const currentValue = deferApply ? pendingValue : value;

  // Wrapper to handle immediate vs deferred changes
  const handleChange = (newValue: any[] | undefined) => {
    if (deferApply) {
      setPendingValue(newValue ?? []);
    } else {
      onChange(newValue);
    }
  };

  // Apply pending changes
  const applyChanges = () => {
    onChange(pendingValue.length > 0 ? pendingValue : undefined);
  };

  // Check if pending value differs from actual value
  const hasPendingChanges = deferApply && JSON.stringify(pendingValue) !== JSON.stringify(value);

  // Find the "Other" option
  const otherOption = options?.find(o => o.label.toLowerCase() === "other");

  // Track collapsed groups
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set((options ?? []).flatMap(o_0 => o_0.group && o_0.groupCollapsed ? [o_0.group] : [])));

  // Check if options have groups
  const hasGroups = useMemo(() => options?.some(o_1 => o_1.group) ?? false, [options]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options || [];
    const query = searchQuery.toLowerCase();
    return (options || []).filter(option => option.label.toLowerCase().includes(query) || option.group?.toLowerCase().includes(query));
  }, [options, searchQuery]);

  // Organize options into groups
  const groupedOptions = useMemo((): OptionGroup[] => {
    if (!hasGroups) return [];
    const groupMap: Record<string, FilterOption[]> = {};
    const ungrouped: FilterOption[] = [];
    for (const option_0 of filteredOptions) {
      if (option_0.group) {
        if (!groupMap[option_0.group]) {
          groupMap[option_0.group] = [];
        }
        groupMap[option_0.group].push(option_0);
      } else {
        ungrouped.push(option_0);
      }
    }
    const groups: OptionGroup[] = Object.entries(groupMap).map(([name, opts]) => ({
      name,
      options: opts
    }));

    // Add ungrouped options at the end if any
    if (ungrouped.length > 0) {
      groups.push({
        name: "Other",
        options: ungrouped
      });
    }
    return groups;
  }, [filteredOptions, hasGroups]);

  // Toggle group collapse state
  const toggleGroupCollapse = (groupName: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Get group selection state (all, some, none)
  const getGroupSelectionState = (group: OptionGroup): "all" | "some" | "none" => {
    const groupValues = group.options.map(o_2 => o_2.value);
    const selectedCount = groupValues.filter(v => currentValue.includes(v)).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === groupValues.length) return "all";
    return "some";
  };

  // Toggle all options in a group
  const toggleGroup = (group_0: OptionGroup) => {
    const state = getGroupSelectionState(group_0);
    const groupValues_0 = group_0.options.map(o_3 => o_3.value);
    let newValue_0: (string | number)[];
    if (state === "all") {
      // Deselect all in group
      newValue_0 = currentValue.filter((v_0: string | number) => !groupValues_0.includes(v_0));
    } else {
      // Select all in group
      const existingNonGroup = currentValue.filter((v_1: string | number) => !groupValues_0.includes(v_1));
      newValue_0 = [...existingNonGroup, ...groupValues_0];
    }
    handleChange(newValue_0.length > 0 ? newValue_0 : undefined);
  };

  // Get visible options (accounting for collapsed groups)
  const visibleOptions = useMemo(() => {
    if (!hasGroups) return filteredOptions;
    const visible: FilterOption[] = [];
    for (const group_1 of groupedOptions) {
      if (searchQuery.trim() || !collapsedGroups.has(group_1.name)) {
        visible.push(...group_1.options);
      }
    }
    return visible;
  }, [hasGroups, filteredOptions, groupedOptions, collapsedGroups, searchQuery]);

  // Reset highlighted index when search query or collapsed groups change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery, collapsedGroups]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (optionsContainerRef.current && visibleOptions.length > 0) {
      const container = optionsContainerRef.current;
      const highlightedItem = container.querySelector<HTMLElement>(`[data-option-index="${highlightedIndex}"]`);
      if (highlightedItem) {
        highlightedItem.scrollIntoView({
          block: "nearest"
        });
      }
    }
  }, [highlightedIndex, visibleOptions.length]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const optionCount = visibleOptions.length;
    if (optionCount === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev_0 => prev_0 >= optionCount - 1 ? 0 : prev_0 + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev_1 => prev_1 <= 0 ? optionCount - 1 : prev_1 - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const option_1 = visibleOptions[highlightedIndex];
      if (option_1) {
        toggleOption(option_1);
      }
    }
  };

  // Initialize state based on incoming value
  useEffect(() => {
    if (otherOption) {
      // Check if there's any value that isn't in the options list (must be a custom "Other" value)
      const customValue = currentValue.find((v_2: any) => !options?.some(o_4 => o_4.value === v_2));
      if (customValue) {
        // We have a custom value, so initialize Other as checked
        setIsOtherChecked(true);
        setOtherValue(customValue);
      } else {
        // No custom value, check if "Other" option is selected
        setIsOtherChecked(currentValue.includes(otherOption.value));
        setOtherValue("");
      }
    }
  }, []);

  // Handle normal option toggle
  const toggleOption = (option_2: FilterOption) => {
    // Special handling for "Other"
    if (option_2.label.toLowerCase() === "other" && enableCustomOther) {
      const newIsChecked = !isOtherChecked;
      setIsOtherChecked(newIsChecked);
      let newValue_1 = [...currentValue];
      if (newIsChecked) {
        // Other was checked - do nothing to the value yet
        // Wait for user to type in the other field
      } else {
        // Other was unchecked - remove any custom values
        newValue_1 = newValue_1.filter((v_3: any) => options?.some(o_5 => o_5.value === v_3));
        setOtherValue("");
      }

      // Send the updated value (or undefined if empty)
      handleChange(newValue_1.length > 0 ? newValue_1 : undefined);
      return;
    }

    // Regular option toggle
    const newValue_2 = [...currentValue];
    const index = newValue_2.indexOf(option_2.value);
    if (index === -1) {
      // Add the option
      newValue_2.push(option_2.value);
    } else {
      // Remove the option
      newValue_2.splice(index, 1);
    }

    // Send the updated value (or undefined if empty)
    handleChange(newValue_2.length > 0 ? newValue_2 : undefined);
  };

  // Handle Other input change
  const handleOtherValueChange = (e_0: React.ChangeEvent<HTMLInputElement>) => {
    const newOtherValue = e_0.target.value;
    setOtherValue(newOtherValue);

    // Filter out any previous custom values and the "Other" option
    const newValue_3 = currentValue.filter((v_4: any) => options?.some(o_6 => o_6.value === v_4 && o_6.label.toLowerCase() !== "other"));

    // Add the new custom value if it exists
    if (newOtherValue) {
      // Prefix with "other: " to indicate it's a custom value
      newValue_3.push(`other: ${newOtherValue}`);
    }

    // Send the updated value (or undefined if empty)
    handleChange(newValue_3.length > 0 ? newValue_3 : undefined);
  };

  // Select all visible options (filtered by search, excluding "Other" if it requires custom input)
  const selectAll = () => {
    const optionsToSelect = filteredOptions.filter(opt => !enableCustomOther || opt.label.toLowerCase() !== "other");
    // Merge with existing values (for options not visible in current filter)
    const existingValues = currentValue.filter((v_5: any) => !optionsToSelect.some(opt_0 => opt_0.value === v_5));
    const allValues = [...existingValues, ...optionsToSelect.map(opt_1 => opt_1.value)];
    handleChange(allValues.length > 0 ? allValues : undefined);
  };

  // Deselect all visible options (filtered by search)
  const selectNone = () => {
    if (searchQuery.trim()) {
      // Only deselect options that are visible in the current filter
      const visibleValues = new Set(filteredOptions.map(opt_2 => opt_2.value));
      const remainingValues = currentValue.filter((v_6: any) => !visibleValues.has(v_6));
      handleChange(remainingValues.length > 0 ? remainingValues : undefined);
    } else {
      // Deselect all
      handleChange(undefined);
      setIsOtherChecked(false);
      setOtherValue("");
    }
  };
  return <div data-handles-arrow-keys>
      {/* Search input for filtering options */}
      {(options?.length || 0) > 0 && <div className="mb-2">
          <Input ref={searchInputRef} value={searchQuery} onChange={e_1 => setSearchQuery(e_1.target.value)} placeholder="Search options..." icon={RiSearchLine} className="h-8 !border-none !bg-transparent text-body !shadow-none !ring-0 focus:!ring-0" onKeyDown={handleKeyDown} />
        </div>}
      {showBulkActions && <div className="flex w-full flex-wrap gap-2 py-1">
          <Button variant="secondary" size="sm" onClick={selectAll} className="text-nowrap rounded-lg bg-background/40 shadow-lg backdrop-blur-sm" type="button">
            {searchQuery.trim() ? "Select Visible" : "Select All"}
          </Button>
          <Button variant="secondary" size="sm" onClick={selectNone} className="text-nowrap rounded-lg bg-background/40 shadow-lg backdrop-blur-sm" type="button">
            {searchQuery.trim() ? "Clear Visible" : "Select None"}
          </Button>
        </div>}
      <div ref={optionsContainerRef} className="relative my-2 grid w-full gap-x-2" style={{
      gridTemplateColumns: `repeat(${columns}, 1fr)`
    }} tabIndex={0} onKeyDown={handleKeyDown}>
        <ScrollShadow.Root>
          <ScrollShadow.Viewport className="max-h-64">
            {filteredOptions.length === 0 ? <div className="px-1 py-2 text-body text-muted-foreground">
                No options match &ldquo;{searchQuery}&rdquo;
              </div> : hasGroups ?
          // Grouped options rendering
          (() => {
            let globalIndex = 0;
            return groupedOptions.map(group_2 => {
              const isCollapsed = !searchQuery.trim() && collapsedGroups.has(group_2.name);
              const selectionState = getGroupSelectionState(group_2);
              return <div key={group_2.name} className="mb-1">
                      {/* Group header with checkbox */}
                      <div className="flex items-center rounded-md px-1 py-1 hover:bg-hover">
                        {/* Collapse/expand toggle */}
                        <button type="button" className="flex cursor-pointer items-center text-foreground-muted hover:text-foreground" onClick={() => toggleGroupCollapse(group_2.name)}>
                          {isCollapsed ? <RiArrowRightSLine className="h-4 w-4" /> : <RiArrowDownSLine className="h-4 w-4" />}
                        </button>
                        {/* Group selection checkbox */}
                        <Checkbox checked={selectionState === "all" ? true : selectionState === "some" ? "indeterminate" : false} className="ml-1" onClick={() => toggleGroup(group_2)} />
                        {/* Group name - clicking toggles collapse */}
                        <span className="ml-2 flex-1 cursor-pointer text-body-strong text-foreground" onClick={() => toggleGroupCollapse(group_2.name)}>
                          {group_2.name}
                        </span>
                        <span className="ml-1 text-caption text-foreground-muted">
                          ({group_2.options.length})
                        </span>
                      </div>

                      {/* Group options */}
                      {!isCollapsed && <div className="ml-5 border-l border-border pl-2">
                          {group_2.options.map(option_3 => {
                    const currentIndex = globalIndex++;
                    const isChecked = enableCustomOther && option_3.label.toLowerCase() === "other" ? isOtherChecked : currentValue.includes(option_3.value);
                    return <div key={`${group_2.name}-${option_3.value}`} data-option-index={currentIndex} className={cx("mb-1 flex cursor-pointer items-center rounded-md px-1 py-0.5 hover:bg-hover", currentIndex === highlightedIndex && "bg-hover")} onClick={() => toggleOption(option_3)}>
                                <Checkbox value={option_3.value} checked={isChecked} className={cx(hasError && hasErrorInput, "pointer-events-none shrink-0")} />
                                <span className="ml-2 min-w-0 cursor-pointer break-words text-body font-normal text-foreground">
                                  {option_3.label}
                                </span>
                              </div>;
                  })}
                        </div>}
                    </div>;
            });
          })() :
          // Flat options rendering (original behavior)
          filteredOptions.map((option_4, index_0) => {
            // Special handling for Other option
            const isChecked_0 = enableCustomOther && option_4.label.toLowerCase() === "other" ? isOtherChecked : currentValue.includes(option_4.value);
            return <div key={index_0} data-option-index={index_0} className={cx("mb-2 flex cursor-pointer items-center rounded-md px-1 py-0.5 hover:bg-hover", index_0 === highlightedIndex && "bg-hover")} onClick={() => toggleOption(option_4)}>
                    <Checkbox value={option_4.value} checked={isChecked_0} className={cx(hasError && hasErrorInput, "pointer-events-none shrink-0")} />
                    <span className="ml-2 min-w-0 cursor-pointer break-words text-body font-normal text-foreground">
                      {option_4.label}
                    </span>
                  </div>;
          })}
          </ScrollShadow.Viewport>
          <ScrollShadow.Top />
          <ScrollShadow.Bottom />
        </ScrollShadow.Root>
      </div>

      {enableCustomOther && isOtherChecked && <div className="mb-4 mt-2">
          <Input placeholder="Please specify other value" value={otherValue} onChange={handleOtherValueChange} className={cx("!border-none !bg-transparent !shadow-none !ring-0 focus:!ring-0", hasError && hasErrorInput)} />
        </div>}

      {hasError && <p className="mt-1.5 text-caption text-status-error-text">
          {errorMessage}
        </p>}

      {deferApply && <div className="mt-2">
          <Button variant="primary" size="sm" onClick={applyChanges} disabled={!hasPendingChanges} className="w-full" type="button">
            Apply
          </Button>
        </div>}
    </div>;
};
export default MultiSelectInput;